import { EDUCATION_ROUTE_NAME, TRAINING_ROUTE_NAME } from '~/features/routeNames';
import { Institution } from '~/types';
import { deleteData, getData, patchData, postData } from '~/utils/service';
import { capitalizeEveryWord } from '../../utils';
import {
  DeleteEvent,
  Events,
  PatchEducationEvent,
  PatchTrainingEvent,
  SubmitEducationEvent,
  SubmitTrainingEvent,
} from './events';
import { AcademicMachineContext, EducationFormValues, TrainingFormValues } from './types';
import {
  educationApiToForm,
  educationFormToApi,
  educationsApiToForm,
  trainingApiToForm,
  trainingFormValuesToApi,
  trainingsApiToForm,
} from './utils';

interface InputArgs {
  input: {
    context: AcademicMachineContext;
    event: Events;
  };
}

export const EDUCATION_API_CALLS = {
  get: async ({ input }: InputArgs): Promise<EducationFormValues[]> => {
    return await getData(EDUCATION_ROUTE_NAME, {
      transformResponse: educationsApiToForm,
      params: { providerId: String(input.context.providerId) },
    });
  },
  create: async ({ input }: InputArgs): Promise<EducationFormValues> => {
    const event = input.event as SubmitEducationEvent;
    return await postData(EDUCATION_ROUTE_NAME, {
      data: event.values,
      params: { providerId: input.context.providerId },
      transformRequest: educationFormToApi,
      transformResponse: educationApiToForm,
    });
  },
  delete: async ({ input }: InputArgs) => {
    const event = input.event as DeleteEvent;
    const providerId = input.context.providerId;
    await deleteData(EDUCATION_ROUTE_NAME, {
      pathParams: { id: event.payload.id.toString() },
      params: { providerId },
    });
  },
  patch: async ({ input }: InputArgs): Promise<EducationFormValues> => {
    const event = input.event as PatchEducationEvent;
    const providerId = input.context.providerId;

    return await patchData(EDUCATION_ROUTE_NAME, {
      pathParams: { id: event.values.id?.toString() || '' },
      params: { providerId },
      data: event.values,
      transformRequest: educationFormToApi,
      transformResponse: educationApiToForm,
    });
  },
};

export const TRAINING_API_CALLS = {
  get: async (): Promise<TrainingFormValues[]> =>
    await getData(TRAINING_ROUTE_NAME, { transformResponse: trainingsApiToForm }),
  create: async ({ input }: InputArgs): Promise<TrainingFormValues> => {
    const event = input.event as SubmitTrainingEvent;
    return await postData(TRAINING_ROUTE_NAME, {
      data: event.values,
      transformRequest: trainingFormValuesToApi,
      transformResponse: trainingApiToForm,
    });
  },
  delete: async ({ input }: InputArgs) => {
    const event = input.event as DeleteEvent;
    return await deleteData(TRAINING_ROUTE_NAME, {
      pathParams: { id: event.payload.id.toString() },
    });
  },
  patch: async ({ input }: InputArgs): Promise<TrainingFormValues> => {
    const event = input.event as PatchTrainingEvent;
    return await patchData(TRAINING_ROUTE_NAME, {
      pathParams: { id: event.values.id?.toString() || '' },
      data: event.values,
      transformRequest: trainingFormValuesToApi,
      transformResponse: trainingApiToForm,
    });
  },
};

export const loadInstitutionOptions = async (input: string) => {
  const response = await getData<Institution[]>('institutions', {
    params: { name: input, limit: 20 },
  });
  const options = response.map((institution) => ({
    value: institution.id.toString(),
    label: capitalizeEveryWord(institution.name),
    institution: institution,
    userAdded: institution.userAddedFlag,
  }));
  return options;
};

export const deleteDetail = async ({
  input,
}: {
  input: {
    id: number;
  };
}) => {
  await deleteData('educationDetails', {
    pathParams: { id: input.id.toString() },
  });
  return { id: input.id };
};
